/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import './BarcodeScanner.css';

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_39,
];

/* ── Tesseract OCR (lazy-loaded) ─────────────────────────────── */
let tesseractWorker = null;
let tesseractReady = false;

async function getOcrWorker() {
  if (tesseractWorker && tesseractReady) return tesseractWorker;
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('eng', 1, {
    logger: () => {}, // suppress logs
  });
  await worker.setParameters({
    tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-',
    tessedit_pageseg_mode: '6', // uniform block of text
  });
  tesseractWorker = worker;
  tesseractReady = true;
  return worker;
}

async function terminateOcrWorker() {
  if (tesseractWorker) {
    try {
      await tesseractWorker.terminate();
    } catch (_) {}
    tesseractWorker = null;
    tesseractReady = false;
  }
}

/* ── Extract barcode-like numbers from OCR text ──────────────── */
function isValidBarcode(code) {
  if (!/^\d+$/.test(code)) return false;
  if (code.length === 13) {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(code[i], 10) * (i % 2 === 0 ? 1 : 3);
    }
    return ((10 - (sum % 10)) % 10) === parseInt(code[12], 10);
  }
  if (code.length === 12) {
    let sum = 0;
    for (let i = 0; i < 11; i++) {
      sum += parseInt(code[i], 10) * (i % 2 === 0 ? 3 : 1);
    }
    return ((10 - (sum % 10)) % 10) === parseInt(code[11], 10);
  }
  if (code.length === 8) {
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      sum += parseInt(code[i], 10) * (i % 2 === 0 ? 3 : 1);
    }
    return ((10 - (sum % 10)) % 10) === parseInt(code[7], 10);
  }
  return true;
}

function extractBarcode(text) {
  // Correct common OCR letter-to-number misinterpretations
  let correctedText = text
    .replace(/[OoQ]/g, '0')
    .replace(/[Ss]/g, '5')
    .replace(/[Il]/g, '1')
    .replace(/[Zz]/g, '2')
    .replace(/[B]/g, '8')
    .replace(/[G]/g, '6');

  const groups = correctedText.match(/\d+/g) || [];
  if (groups.length === 0) return null;

  let candidates = [];
  const targets = [13, 12, 8];

  for (let i = 0; i < groups.length; i++) {
    let concat = '';
    for (let j = i; j < Math.min(i + 8, groups.length); j++) {
      concat += groups[j];
      if (concat.length >= 8 && concat.length <= 14) {
        candidates.push(concat);
      }
      if (concat.length > 14) break;
    }
  }

  const long = groups.filter(g => g.length >= 8);
  candidates.push(...long);

  const all = groups.join('');
  if (all.length >= 8 && all.length <= 14) candidates.push(all);

  if (candidates.length === 0) return null;
  candidates = [...new Set(candidates)];

  const validCandidates = candidates.filter(c => targets.includes(c.length) && isValidBarcode(c));
  if (validCandidates.length > 0) {
    return validCandidates.sort((a, b) => b.length - a.length)[0];
  }

  // Fallback: no valid checksum found
  const targetCandidates = candidates.filter(c => targets.includes(c.length));
  if (targetCandidates.length > 0) {
    return targetCandidates.sort((a, b) => b.length - a.length)[0];
  }

  return candidates.sort((a, b) => b.length - a.length)[0];
}

/* ── Crop image to barcode area before OCR ───────────────────── */
async function cropImageForBarcode(file) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Crop: ครอบคลุมทั้งรูปแนวกว้าง ตัดแค่ขอบบน-ล่าง
      const cropX = Math.floor(img.width * 0.02);
      const cropY = Math.floor(img.height * 0.10); // เริ่ม 10% จากบน
      const cropW = Math.floor(img.width * 0.96);
      const cropH = Math.floor(img.height * 0.85); // ครอบคลุมถึง 95%
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext('2d');
      ctx.filter = 'contrast(1.8) brightness(1.05) grayscale(1)';
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob || file), 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

/* ── Use native BarcodeDetector if available ─────────────────── */
async function tryNativeBarcodeDetector(file) {
  if (!('BarcodeDetector' in window)) return null;
  try {
    const detector = new window.BarcodeDetector({ formats: ['ean_13', 'ean_8', 'code_128'] });
    const bitmap = await createImageBitmap(file);
    const barcodes = await detector.detect(bitmap);
    if (barcodes.length > 0) return barcodes[0].rawValue;
  } catch (_) {}
  return null;
}

export default function BarcodeScanner({ onScanSuccess, onClose }) {
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const onScanSuccessRef = useRef(onScanSuccess);
  const onCloseRef = useRef(onClose);
  const mountedRef = useRef(true);
  const lastScannedRef = useRef('');
  const scanCooldownRef = useRef(false);

  const [isStarted, setIsStarted] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [scanError, setScanError] = useState('');
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [camIndex, setCamIndex] = useState(0);
  const [scanFlash, setScanFlash] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);

  useEffect(() => { onScanSuccessRef.current = onScanSuccess; }, [onScanSuccess]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  /* ── Stop camera helper ── */
  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      const state = s.getState();
      if (state === 2 || state === 3) await s.stop();
      await s.clear();
    } catch (_) {}
    scannerRef.current = null;
    if (mountedRef.current) setIsStarted(false);
  }, []);

  /* ── Start camera helper ── */
  const startCamera = useCallback(async (cameraConstraint) => {
    setScanError('');
    await stopScanner();

    await new Promise(r => setTimeout(r, 80));
    if (!document.getElementById('bs-reader')) return;

    try {
      const scanner = new Html5Qrcode('bs-reader', {
        verbose: false,
        formatsToSupport: SUPPORTED_FORMATS,
      });
      scannerRef.current = scanner;

      await scanner.start(
        cameraConstraint,
        {
          fps: 15,
          // กรอบแนวนอนพอดีบาร์โค้ด: กว้าง 80% ของหน้าจอ, สูง ~35%
          qrbox: (w, h) => ({
            width:  Math.floor(w * 0.80),
            height: Math.floor(Math.min(w, h) * 0.35),
          }),
          formatsToSupport: SUPPORTED_FORMATS,
          // ปิดการใช้ Native BarcodeDetector เพราะบางเครื่องมักจะอ่านเลขเพี้ยนหรือตัดเลข 0 ออก
          experimentalFeatures: { useBarCodeDetectorIfSupported: false },
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            advanced: [
              { zoom: 2.0 },
              { focusMode: 'continuous' }
            ],
          },
          aspectRatio: 1.7778,
        },
        (text) => {
          if (scanCooldownRef.current || text === lastScannedRef.current) return;
          lastScannedRef.current = text;
          scanCooldownRef.current = true;
          setTimeout(() => { scanCooldownRef.current = false; }, 1500);

          handleScanResult(text);
        },
        () => {}
      );

      if (mountedRef.current) {
        setIsStarted(true);
        try {
          const track = scanner.getRunningTrackCapabilities();
          setHasTorch(!!(track && track.torch));
        } catch (_) {}
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (mountedRef.current) {
        setScanError('ไม่สามารถเปิดกล้องได้ค่ะ กรุณาอนุญาต Camera permission ในเบราว์เซอร์แล้วลองอีกครั้ง');
      }
    }
  }, [stopScanner]);

  /* ── Auto-start on mount ── */
  useEffect(() => {
    mountedRef.current = true;

    Html5Qrcode.getCameras()
      .then(devices => {
        if (!mountedRef.current) return;
        if (devices && devices.length) {
          setCameras(devices);
          const backIdx = devices.findIndex(d =>
            d.label.toLowerCase().includes('back') ||
            d.label.toLowerCase().includes('environment') ||
            d.label.toLowerCase().includes('rear') ||
            d.label.toLowerCase().includes('หลัง')
          );
          const idx = backIdx >= 0 ? backIdx : 0;
          setCamIndex(idx);
          startCamera({ deviceId: { exact: devices[idx].id } });
        } else {
          startCamera({ facingMode: 'environment' });
        }
      })
      .catch(() => {
        if (!mountedRef.current) return;
        startCamera({ facingMode: 'environment' });
      });

    return () => {
      mountedRef.current = false;
      stopScanner();
      terminateOcrWorker();
    };
  }, []);

  /* ── Toggle torch ── */
  const toggleTorch = useCallback(async () => {
    if (!scannerRef.current) return;
    try {
      const next = !torchOn;
      await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (_) {}
  }, [torchOn]);

  /* ── Switch camera ── */
  const switchCamera = useCallback(async () => {
    if (cameras.length < 2) return;
    const next = (camIndex + 1) % cameras.length;
    setCamIndex(next);
    await startCamera({ deviceId: { exact: cameras[next].id } });
  }, [cameras, camIndex, startCamera]);

  /* ── Scan success helper ── */
  const handleScanResult = useCallback((text) => {
    if (!text) return;
    if (mountedRef.current) setScanFlash(true);
    setTimeout(() => { if (mountedRef.current) setScanFlash(false); }, 300);
    try { navigator.vibrate && navigator.vibrate([60, 30, 60]); } catch (_) {}
    onScanSuccessRef.current && onScanSuccessRef.current(text);
  }, []);

  const scanFileWithFallback = useCallback(async (file) => {
    setScanError('');
    setOcrLoading(true);

    try {
      const tmp = new Html5Qrcode('bs-file-reader', { verbose: false, formatsToSupport: SUPPORTED_FORMATS });
      try {
        const result = await tmp.scanFile(file, false);
        handleScanResult(result);
        setOcrLoading(false);
        return;
      } catch (_) {
        // Continue to fallback
      } finally {
        await tmp.clear().catch(() => {});
      }
    } catch (_) {}

    try {
      const tmp2 = new Html5Qrcode('bs-file-reader', { verbose: false, formatsToSupport: SUPPORTED_FORMATS });
      try {
        const result2 = await tmp2.scanFileV2(file, false);
        handleScanResult(result2.decodedText);
        setOcrLoading(false);
        return;
      } catch (_) {
        // Continue to fallback
      } finally {
        await tmp2.clear().catch(() => {});
      }
    } catch (_) {}

    // Step 3: Native BarcodeDetector API (Chrome/Android)
    const native = await tryNativeBarcodeDetector(file);
    if (native) {
      setOcrLoading(false);
      handleScanResult(native);
      return;
    }

    // Step 4: Tesseract OCR — ลอง crop ก่อน ถ้าไม่เจอให้ลองรูปเต็ม
    try {
      const worker = await getOcrWorker();

      // Pass 1: OCR บน cropped image
      const croppedBlob = await cropImageForBarcode(file);
      const { data: { text: text1 } } = await worker.recognize(croppedBlob);
      const extracted1 = extractBarcode(text1);
      if (extracted1) {
        setOcrLoading(false);
        handleScanResult(extracted1);
        return;
      }

      // Pass 2: OCR บนรูปเต็ม (กรณี barcode อยู่ขอบรูป)
      const { data: { text: text2 } } = await worker.recognize(file);
      const extracted2 = extractBarcode(text2);
      setOcrLoading(false);
      if (extracted2) {
        handleScanResult(extracted2);
      } else {
        if (mountedRef.current) setScanError('ไม่พบเลขบาร์โค้ดในภาพนี้ค่ะ กรุณาถ่ายให้เห็นตัวเลขบาร์โค้ดชัดๆ');
      }
    } catch (ocrErr) {
      setOcrLoading(false);
      if (mountedRef.current) setScanError('ไม่สามารถอ่านข้อมูลจากภาพได้ค่ะ');
    }
  }, [handleScanResult]);

  /* ── Scan from file upload ── */
  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    
    setGalleryImages(prev => {
      const next = [{ url, file }, ...prev];
      if (next.length > 8) {
        // BUG-2 Fix: Revoke URL for removed items
        next.slice(8).forEach(item => URL.revokeObjectURL(item.url));
      }
      return next.slice(0, 8);
    });

    await scanFileWithFallback(file);
    e.target.value = '';
  }, [scanFileWithFallback]);

  /* ── Re-scan from gallery ── */
  const handleScanGalleryImage = useCallback(async (item) => {
    await scanFileWithFallback(item.file);
  }, [scanFileWithFallback]);

  const handleClose = useCallback(async () => {
    await stopScanner();
    onCloseRef.current && onCloseRef.current();
  }, [stopScanner]);

  /* ── Render ── */
  return (
    <div className='bs-overlay'>
      {scanFlash && <div className='bs-scan-flash' />}

      {/* Top bar */}
      <div className='bs-topbar'>
        <button className='bs-close-btn' onClick={handleClose} aria-label='ปิด'>
          <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5'>
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
        <div className='bs-topbar-icons'>
          {hasTorch && (
            <button className='bs-icon-btn' title='ไฟฉาย' onClick={toggleTorch}>
              <svg width='22' height='22' viewBox='0 0 24 24' fill={torchOn ? '#FFD700' : 'none'} stroke={torchOn ? '#FFD700' : 'white'} strokeWidth='2'>
                <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2' />
              </svg>
            </button>
          )}
          {cameras.length > 1 && (
            <button className='bs-icon-btn' title='สลับกล้อง' onClick={switchCamera}>
              <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2'>
                <path d='M20 7h-4l-2-3H10L8 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z' />
                <circle cx='12' cy='13' r='3' />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Camera viewfinder */}
      <div className='bs-camera-area'>
        <div id='bs-reader' className='bs-reader'></div>

        {isStarted && <div className='bs-scanline' />}

        {isStarted && (
          <div className='bs-scan-hint'>วางบาร์โค้ดในกรอบ — สแกนอัตโนมัติค่ะ</div>
        )}

        {!isStarted && !scanError && (
          <div className='bs-camera-placeholder'>
            <div className='bs-viewfinder-corners'>
              <span className='bs-corner bs-tl'></span>
              <span className='bs-corner bs-tr'></span>
              <span className='bs-corner bs-bl'></span>
              <span className='bs-corner bs-br'></span>
            </div>
            <p className='bs-hint-text'>กำลังเปิดกล้อง...</p>
          </div>
        )}

        {!isStarted && scanError && (
          <div className='bs-camera-placeholder'>
            <div className='bs-viewfinder-corners'>
              <span className='bs-corner bs-tl'></span>
              <span className='bs-corner bs-tr'></span>
              <span className='bs-corner bs-bl'></span>
              <span className='bs-corner bs-br'></span>
            </div>
            <p className='bs-hint-text bs-hint-error'>{scanError}</p>
            <button className='bs-retry-btn' onClick={() => startCamera({ facingMode: 'environment' })}>
              ลองอีกครั้ง
            </button>
          </div>
        )}
      </div>

      {/* Error / OCR loading bar */}
      {ocrLoading && (
        <div className='bs-ocr-bar'>
          <div className='bs-ocr-spinner' />
          <span>กำลังอ่านข้อมูลจากรูปภาพ...</span>
        </div>
      )}
      {!ocrLoading && isStarted && scanError && (
        <div className='bs-error-bar'>
          <span>{scanError}</span>
          <button onClick={() => setScanError('')}>✕</button>
        </div>
      )}

      {/* Gallery section */}
      <div className='bs-gallery-section'>
        <div className='bs-gallery-header'>
          <span className='bs-gallery-title'>อ่านจากรูปภาพ</span>
          <button className='bs-gallery-all' onClick={() => fileInputRef.current && fileInputRef.current.click()}>
            เลือกรูป &rsaquo;
          </button>
        </div>

        <div className='bs-gallery-row'>
          <button
            className='bs-gallery-upload-tile'
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            aria-label='อัปโหลดภาพ'
          >
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#aaa' strokeWidth='2'>
              <rect x='3' y='3' width='18' height='18' rx='2' />
              <circle cx='8.5' cy='8.5' r='1.5' />
              <polyline points='21 15 16 10 5 21' />
            </svg>
            <span>อัปโหลด</span>
          </button>

          {galleryImages.map((img, idx) => (
            <button key={idx} className='bs-gallery-thumb' onClick={() => handleScanGalleryImage(img)} title='สแกนภาพนี้'>
              <img src={img.url} alt={`gallery-${idx}`} />
              <span className='bs-gallery-scan-badge'>สแกน</span>
            </button>
          ))}
        </div>
      </div>

      <input ref={fileInputRef} type='file' accept='image/*' capture='environment' style={{ display: 'none' }} onChange={handleFileChange} />
      <div id='bs-file-reader' style={{ display: 'none' }}></div>
    </div>
  );
}
